import { ChevronRightIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { DragEvent as ReactDragEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { DocumentationPageTreeNode } from '../../services/documentationApi';
import { useDocumentationShell } from './documentationShell';

export const DOCUMENTATION_TREE_ROOT_ID = '__space_root__';

export type TreeDropPosition = 'before' | 'after' | 'into';

export type DocumentationTreeMenuAction =
  | 'rename'
  | 'move'
  | 'moveUp'
  | 'moveDown'
  | 'makeRoot'
  | 'duplicate'
  | 'save'
  | 'edit'
  | 'view'
  | 'publish'
  | 'archive'
  | 'delete';

type DocumentationPageTreeProps = {
  nodes: DocumentationPageTreeNode[];
  activeId?: string;
  /** Quando true, o nó raiz (documento) está selecionado. */
  rootActive?: boolean;
  spaceId: string;
  spaceName: string;
  canWrite: boolean;
  onDrop: (dragId: string, targetId: string, position: TreeDropPosition) => void;
  onMenuAction: (pageId: string, action: DocumentationTreeMenuAction) => void;
  onRename: (pageId: string, title: string) => void;
  renamingId: string | null;
  onCancelRename: () => void;
};

type MenuState = { pageId: string; x: number; y: number } | null;

type DragOverState = { targetId: string; position: TreeDropPosition } | null;

function collectDescendantIds(node: DocumentationPageTreeNode): Set<string> {
  const ids = new Set<string>([node.id]);
  for (const child of node.children ?? []) {
    collectDescendantIds(child).forEach((id) => ids.add(id));
  }
  return ids;
}

function findNode(nodes: DocumentationPageTreeNode[], id: string): DocumentationPageTreeNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findNode(node.children ?? [], id);
    if (found) return found;
  }
  return null;
}

function collectExpandableIds(nodes: DocumentationPageTreeNode[]): string[] {
  const ids: string[] = [];
  for (const node of nodes) {
    if (node.children?.length) {
      ids.push(node.id);
      ids.push(...collectExpandableIds(node.children));
    }
  }
  return ids;
}

function TreeNodeRow({
  node,
  activeId,
  spaceId,
  canWrite,
  depth,
  dragOver,
  renamingId,
  menuOpenId,
  expandedIds,
  onToggleExpand,
  onDragOverChange,
  onDrop,
  onOpenMenu,
  onRename,
  onCancelRename,
}: {
  node: DocumentationPageTreeNode;
  activeId?: string;
  spaceId: string;
  canWrite: boolean;
  depth: number;
  dragOver: DragOverState;
  renamingId: string | null;
  menuOpenId: string | null;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  onDragOverChange: (state: DragOverState) => void;
  onDrop: (dragId: string, targetId: string, position: TreeDropPosition) => void;
  onOpenMenu: (pageId: string, x: number, y: number) => void;
  onRename: (pageId: string, title: string) => void;
  onCancelRename: () => void;
}) {
  const shell = useDocumentationShell();
  const [renameValue, setRenameValue] = useState(node.title);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasChildren = Boolean(node.children?.length);
  const isExpanded = expandedIds.has(node.id);

  useEffect(() => {
    if (renamingId === node.id) {
      setRenameValue(node.title);
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [renamingId, node.id, node.title]);

  function resolvePosition(event: ReactDragEvent<HTMLElement>): TreeDropPosition {
    const rect = event.currentTarget.getBoundingClientRect();
    const y = event.clientY - rect.top;
    const ratio = y / rect.height;
    if (ratio < 0.25) return 'before';
    if (ratio > 0.75) return 'after';
    return 'into';
  }

  function handleDragOver(event: ReactDragEvent<HTMLElement>): void {
    if (!canWrite) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    onDragOverChange({ targetId: node.id, position: resolvePosition(event) });
  }

  function handleDrop(event: ReactDragEvent<HTMLElement>): void {
    if (!canWrite) return;
    event.preventDefault();
    event.stopPropagation();
    const dragId = event.dataTransfer.getData('text/docs-page-id');
    if (!dragId || dragId === node.id) {
      onDragOverChange(null);
      return;
    }
    onDrop(dragId, node.id, resolvePosition(event));
    onDragOverChange(null);
  }

  function submitRename(event?: FormEvent): void {
    event?.preventDefault();
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === node.title) {
      onCancelRename();
      return;
    }
    onRename(node.id, trimmed);
  }

  const isOver = dragOver?.targetId === node.id;
  const dropClass = isOver ? ` is-drop-${dragOver.position}` : '';

  return (
    <li>
      <div
        className={`docs-nav-row${activeId === node.id ? ' is-active' : ''}${dropClass}`}
        style={{ paddingLeft: `${0.35 + depth * 0.75}rem` }}
        draggable={canWrite && renamingId !== node.id}
        onDragStart={(event) => {
          event.dataTransfer.setData('text/docs-page-id', node.id);
          event.dataTransfer.effectAllowed = 'move';
        }}
        onDragOver={handleDragOver}
        onDragLeave={() => {
          if (dragOver?.targetId === node.id) onDragOverChange(null);
        }}
        onDrop={handleDrop}
      >
        {hasChildren ? (
          <button
            type="button"
            className={`docs-nav-expand${isExpanded ? ' is-expanded' : ''}`}
            title={isExpanded ? 'Recolher' : 'Expandir'}
            aria-label={isExpanded ? `Recolher ${node.title}` : `Expandir ${node.title}`}
            aria-expanded={isExpanded}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleExpand(node.id);
            }}
          >
            <ChevronRightIcon className="h-3.5 w-3.5" />
          </button>
        ) : (
          <span className="docs-nav-expand-spacer" aria-hidden />
        )}

        {renamingId === node.id ? (
          <form className="docs-nav-rename" onSubmit={submitRename}>
            <input
              ref={inputRef}
              className="erp-input docs-nav-rename__input"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={() => submitRename()}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  onCancelRename();
                }
              }}
              maxLength={300}
            />
          </form>
        ) : (
          <Link
            to={`${shell.basePath}/${spaceId}/pages/${node.id}`}
            className="docs-nav-link"
            draggable={false}
          >
            <span className="docs-nav-link__title">{node.title}</span>
            {node.status === 'Draft' ? <span className="docs-nav-badge">rascunho</span> : null}
          </Link>
        )}

        {canWrite && renamingId !== node.id ? (
          <button
            type="button"
            className={`docs-nav-menu-btn${menuOpenId === node.id ? ' is-open' : ''}`}
            title="Mais opções"
            aria-label={`Opções de ${node.title}`}
            aria-haspopup="menu"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              onOpenMenu(node.id, rect.right, rect.bottom + 4);
            }}
          >
            <EllipsisVerticalIcon className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {hasChildren && isExpanded ? (
        <ul className="docs-nav-tree docs-nav-tree--nested">
          {node.children.map((child) => (
            <TreeNodeRow
              key={child.id}
              node={child}
              activeId={activeId}
              spaceId={spaceId}
              canWrite={canWrite}
              depth={depth + 1}
              dragOver={dragOver}
              renamingId={renamingId}
              menuOpenId={menuOpenId}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              onDragOverChange={onDragOverChange}
              onDrop={onDrop}
              onOpenMenu={onOpenMenu}
              onRename={onRename}
              onCancelRename={onCancelRename}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export function DocumentationPageTree({
  nodes,
  activeId,
  rootActive = false,
  spaceId,
  spaceName,
  canWrite,
  onDrop,
  onMenuAction,
  onRename,
  renamingId,
  onCancelRename,
}: DocumentationPageTreeProps) {
  const [menu, setMenu] = useState<MenuState>(null);
  const [dragOver, setDragOver] = useState<DragOverState>(null);
  const shell = useDocumentationShell();
  const [rootExpanded, setRootExpanded] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set(collectExpandableIds(nodes)));
  const menuRef = useRef<HTMLDivElement>(null);
  const nodesKey = nodes.map((n) => n.id).join(',');

  useEffect(() => {
    setExpandedIds((prev) => {
      const all = collectExpandableIds(nodes);
      if (prev.size === 0) return new Set(all);
      const merged = new Set(prev);
      all.forEach((id) => {
        if (!merged.has(id)) merged.add(id);
      });
      return merged;
    });
  }, [nodesKey, nodes]);

  useEffect(() => {
    if (!menu) return;

    function close() {
      setMenu(null);
    }

    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') close();
    }

    function onPointer(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        close();
      }
    }

    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [menu]);

  function handleDrop(dragId: string, targetId: string, position: TreeDropPosition): void {
    if (targetId === DOCUMENTATION_TREE_ROOT_ID) {
      onDrop(dragId, DOCUMENTATION_TREE_ROOT_ID, 'into');
      return;
    }
    const dragged = findNode(nodes, dragId);
    if (!dragged) return;
    if (collectDescendantIds(dragged).has(targetId)) return;
    onDrop(dragId, targetId, position);
  }

  function toggleExpand(id: string): void {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function runAction(action: DocumentationTreeMenuAction): void {
    if (!menu) return;
    const pageId = menu.pageId;
    setMenu(null);
    onMenuAction(pageId, action);
  }

  const menuNode = menu ? findNode(nodes, menu.pageId) : null;
  const rootDropClass =
    dragOver?.targetId === DOCUMENTATION_TREE_ROOT_ID ? ` is-drop-${dragOver.position}` : '';

  return (
    <>
      <ul className="docs-nav-tree">
        <li>
          <div
            className={`docs-nav-row docs-nav-row--root${rootActive ? ' is-active' : ''}${rootDropClass}`}
            onDragOver={(event) => {
              if (!canWrite) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
              setDragOver({ targetId: DOCUMENTATION_TREE_ROOT_ID, position: 'into' });
            }}
            onDragLeave={() => {
              if (dragOver?.targetId === DOCUMENTATION_TREE_ROOT_ID) setDragOver(null);
            }}
            onDrop={(event) => {
              if (!canWrite) return;
              event.preventDefault();
              event.stopPropagation();
              const dragId = event.dataTransfer.getData('text/docs-page-id');
              if (dragId) handleDrop(dragId, DOCUMENTATION_TREE_ROOT_ID, 'into');
              setDragOver(null);
            }}
          >
            {nodes.length > 0 ? (
              <button
                type="button"
                className={`docs-nav-expand${rootExpanded ? ' is-expanded' : ''}`}
                title={rootExpanded ? 'Recolher' : 'Expandir'}
                aria-label={rootExpanded ? `Recolher ${spaceName}` : `Expandir ${spaceName}`}
                aria-expanded={rootExpanded}
                onClick={() => setRootExpanded((v) => !v)}
              >
                <ChevronRightIcon className="h-3.5 w-3.5" />
              </button>
            ) : (
              <span className="docs-nav-expand-spacer" aria-hidden />
            )}
            <Link
              to={`${shell.basePath}/${spaceId}`}
              className="docs-nav-link docs-nav-link--root"
              title={spaceName}
            >
              <span className="docs-nav-link__title">{spaceName}</span>
            </Link>
          </div>

          {rootExpanded && nodes.length > 0 ? (
            <ul className="docs-nav-tree docs-nav-tree--nested">
              {nodes.map((node) => (
                <TreeNodeRow
                  key={node.id}
                  node={node}
                  activeId={activeId}
                  spaceId={spaceId}
                  canWrite={canWrite}
                  depth={1}
                  dragOver={dragOver}
                  renamingId={renamingId}
                  menuOpenId={menu?.pageId ?? null}
                  expandedIds={expandedIds}
                  onToggleExpand={toggleExpand}
                  onDragOverChange={setDragOver}
                  onDrop={handleDrop}
                  onOpenMenu={(pageId, x, y) => setMenu({ pageId, x, y })}
                  onRename={onRename}
                  onCancelRename={onCancelRename}
                />
              ))}
            </ul>
          ) : null}
        </li>
      </ul>

      {menu && menuNode ? (
        <div
          ref={menuRef}
          className="docs-tree-menu"
          role="menu"
          style={{ top: menu.y, left: Math.min(menu.x - 160, window.innerWidth - 180) }}
        >
          <button type="button" role="menuitem" onClick={() => runAction('rename')}>
            Renomear
          </button>
          <button type="button" role="menuitem" onClick={() => runAction('moveUp')}>
            Mover para cima
          </button>
          <button type="button" role="menuitem" onClick={() => runAction('moveDown')}>
            Mover para baixo
          </button>
          <button type="button" role="menuitem" onClick={() => runAction('move')}>
            Mover para…
          </button>
          <button type="button" role="menuitem" onClick={() => runAction('makeRoot')}>
            Tornar raiz
          </button>
          <button type="button" role="menuitem" onClick={() => runAction('duplicate')}>
            Fazer cópia
          </button>
          <button type="button" role="menuitem" onClick={() => runAction('save')}>
            Salvar
          </button>
          <div className="docs-tree-menu__sep" />
          <button type="button" role="menuitem" onClick={() => runAction('edit')}>
            Editar
          </button>
          {menuNode.status === 'Published' ? (
            <button type="button" role="menuitem" onClick={() => runAction('view')}>
              Visualizar
            </button>
          ) : null}
          {menuNode.status === 'Draft' || menuNode.status === 'Archived' || menuNode.status === 'Expired' ? (
            <button type="button" role="menuitem" onClick={() => runAction('publish')}>
              Publicar
            </button>
          ) : null}
          {menuNode.status === 'Published' || menuNode.status === 'Expired' ? (
            <button type="button" role="menuitem" onClick={() => runAction('archive')}>
              Arquivar
            </button>
          ) : null}
          <div className="docs-tree-menu__sep" />
          <button
            type="button"
            role="menuitem"
            className="docs-tree-menu__danger"
            onClick={() => runAction('delete')}
          >
            Excluir
          </button>
        </div>
      ) : null}
    </>
  );
}
